import {
  Button,
  TextField,
  Typography,
  makeStyles,
} from "@material-ui/core";
import React, { useState } from "react";

import {
  isDifc,
  isDigipub,
  isHotStar,
  isNSE,
  isRv,
  isViacom,
  isSebi,
  isStar,
  isWebnyay,
  isMuthoot,
  isMSME,
  isArb,
  isNalsar,
} from "Routes/Routes";
import { useDispatch } from "react-redux";
import { deleteMeeting, editMeetingTitle } from "slices/appointmentSlice";
import {
  getMeetingTimeFormatwithDateFirst,
  getMeetingTimeFormatwithDateFirstDIFC,
} from "slices/date";

const useStyles = makeStyles((theme) => ({
  paper: {
    margin: "3rem auto",
    minHeight: "10rem",
    // fit the viewport (less the 3rem top/bottom margin) and scroll inside
    maxHeight: "calc(100vh - 6rem)",
    boxSizing: "border-box",
    width: "92%",
    maxWidth: 560,
    backgroundColor: theme.palette.background.paper,
    padding: theme.spacing(3, 4),
    outline: "none",
    borderRadius: "0.4rem",
    overflowY: "auto",
    [theme.breakpoints.down("xs")]: {
      padding: theme.spacing(2.5, 2),
    },
  },
  heading: {
    textAlign: "center",
    marginBottom: theme.spacing(1.5),
  },
  // one detail per row: label on the left, value on the right (stacked on phones)
  row: {
    display: "grid",
    gridTemplateColumns: "10.5rem 1fr",
    columnGap: theme.spacing(2),
    alignItems: "baseline",
    padding: theme.spacing(1.25, 0),
    borderBottom: `1px solid ${theme.palette.divider}`,
    [theme.breakpoints.down("xs")]: {
      gridTemplateColumns: "1fr",
      rowGap: theme.spacing(0.25),
    },
  },
  label: {
    color: "#000",
    fontSize: "0.95rem",
    fontWeight: 500,
  },
  value: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    minWidth: 0,
  },
  valueText: {
    color: theme.palette.common.grey1,
    fontSize: "1rem",
    lineHeight: 1.5,
    overflowWrap: "anywhere",
  },
  noWrap: {
    whiteSpace: "nowrap",
  },
  actions: {
    display: "flex",
    justifyContent: "center",
    marginTop: theme.spacing(2.5),
  },

  greyColor: {
    color: theme.palette.common.grey1,
  },
  hoverButton: {
    background: "#3F51B5",
    color: "#FFFFFF",
    border: "1px solid transparent",
    "&:hover": {
      backgroundColor: "#FFFFFF",
      color: "#3F51B5",
      border: "1px solid #3F51B5",
    },
  },
}));

export default function EventDisplay({
  eventDetails,
  handleEventDetailsClose,
  sources,
  profile,
}) {
  const classes = useStyles();
  const dispatch = useDispatch();

  const {
    title,
    extendedProps: {
      appointmentId,
      dispute,
      meetingId,
      appointmentName,
      isPastEvent,
      ext_link,
      tiac_custom_dispute_id,
      group,
    },
  } = eventDetails.event["_def"];

  const onCancel = () => {
    dispatch(deleteMeeting(meetingId, dispute)).then(() => {
      handleEventDetailsClose();
    });
  };
  // Worked out during render, not in an effect: Info copies its value into state on
  // its first render, so a value set afterwards never showed ("Hearing Details" blank).
  const meeting = sources[0]["events"].find(
    (e) => e.meeting.id == meetingId
  )?.meeting;
  const by = meeting
    ? meeting.vc_type == "zoom"
      ? "Zoom VC"
      : "In-built VC"
    : undefined;

  // Start and end in the user's timezone, in the platform's meeting-time format.
  const formatHearingTime = (time) =>
    time
      ? (isDifc
          ? getMeetingTimeFormatwithDateFirstDIFC
          : getMeetingTimeFormatwithDateFirst)(time, profile?.timezone)
      : "";
  // Hearing Details = how it is held, with when it runs on the line below.
  // Each time stays on one line, so a narrow modal wraps only at "to".
  const hearingDetails = (
    <>
      {by}
      {by && <br />}
      <span className={classes.noWrap}>
        {formatHearingTime(meeting?.start ?? eventDetails.event.start)}
      </span>{" "}
      to{" "}
      <span className={classes.noWrap}>
        {formatHearingTime(meeting?.end ?? eventDetails.event.end)}
      </span>
    </>
  );

  let data;
  if (isHotStar || isStar || isMuthoot) {
    data = [
      {
        title:
          isWebnyay || isViacom || isDigipub
            ? "Grievance"
            : isRv
            ? "Case"
            : "Dispute",
        value: dispute,
      },
      {
        title: "Meeting link",
        value: ext_link,
      },
      {
        title: "Meeting ID",
        value: meetingId,
      },
    ];
  } else if (isDifc) {
    data = [
      {
        title:
          isWebnyay || isViacom || isDigipub
            ? "Grievance"
            : isRv || isNSE
            ? "Case"
            : "Dispute",
        value: tiac_custom_dispute_id ? tiac_custom_dispute_id : dispute,
      },
      {
        title: "Event title",
        value: title,
        is_editable: true,
      },
      {
        title: isWebnyay || isRv ? "Hearing Details " : " ",
        value: hearingDetails,
      },
      {
        title: "Meeting ID",
        value: meetingId,
      },
      {
        title: "Claimant vs Respondent",
        value: appointmentName,
      },
    ];
  } else if (isRv || isWebnyay) {
    data = [
      {
        title:
          isWebnyay || isViacom || isDigipub
            ? "Grievance"
            : isRv || isNSE
            ? "Case"
            : "Dispute",
        value: tiac_custom_dispute_id ? tiac_custom_dispute_id : dispute,
      },
      {
        title: "Event title",
        value: title,
      },
      {
        title: isWebnyay || isRv ? "Hearing Details " : " ",
        value: hearingDetails,
      },
      {
        title: "Meeting ID",
        value: meetingId,
      },
      {
        title: "Claimant vs Respondent",
        value: appointmentName,
      },
    ];
  } else {
    data = [
      {
        title:
          isWebnyay || isViacom || isDigipub
            ? "Grievance"
            : isRv || isNSE
            ? "Case"
            : "Dispute",
        value: tiac_custom_dispute_id ? tiac_custom_dispute_id : dispute,
      },
      {
        title: "Event title",
        value: title,
      },
      {
        title: "Meeting ID",
        value: meetingId,
      },
      {
        title: "Claimant vs Respondent",
        value: appointmentName,
      },
    ];
  }

  const isScheduleButtonDisabled =
    ((isSebi || isMSME) &&
      profile.profile_type !== "mediator" &&
      profile.profile_type !== "arbitrator_rv" &&
      profile.profile_type !== "case_manager") ||
    (isDifc &&
      profile.profile_type !== "case_manager" &&
      profile.profile_type !== "sub_admin_tiac_rv") ||
    (isArb &&
      profile.profile_type !== "sub_admin_tiac_rv" &&
      profile.profile_type !== "case_manager"
    ) || 
    (isNalsar &&
      profile.profile_type !== "sub_admin_tiac_rv" &&
      profile.profile_type !== "case_manager"
    );

  return (
    <div className={classes.paper}>
      <Typography variant="h5" className={classes.heading}>
        Event Details
      </Typography>
      {data.map((singleItem) => {
        return (
          <Info
            title={singleItem.title}
            value={singleItem.value}
            key={singleItem.title}
            is_editable={singleItem.is_editable}
            dispatch={dispatch}
            meetingId={meetingId}
            handleEventDetailsClose={handleEventDetailsClose}
            classes={classes}
          />
        );
      })}
      <div className={classes.actions}>
        <Button
          disabled={isPastEvent || isScheduleButtonDisabled}
          color="primary"
          variant="contained"
          onClick={onCancel}
          className={
            isHotStar || isStar || isMuthoot ? classes.hoverButton : null
          }
        >
          {isPastEvent ? "Completed" : "Cancel Meeting"}
        </Button>
      </div>
    </div>
  );
}

const Info = ({ title, value, is_editable, dispatch, meetingId, handleEventDetailsClose, classes }) => {
  const [name, setName] = useState(value);

  const onNameChange = (e) => {
    setName(e.target.value);
  };
  const [editMode, setEditMode] = useState(false);
  const toogleEdit = () => {
    if(editMode){
      // submit
      dispatch(editMeetingTitle(meetingId, name)).then(()=>{
        handleEventDetailsClose()
      })
    }
    setEditMode(!editMode);

  };

  return (
    <div className={classes.row}>
      <Typography className={classes.label}>{title}</Typography>
      <div className={classes.value}>
        {editMode ? (
          <TextField
            label={"Title"}
            variant="outlined"
            size="small"
            value={name}
            onChange={onNameChange}
          />
        ) : (
          <Typography component="div" className={classes.valueText}>
            {name}
          </Typography>
        )}
        {is_editable && (
          <Button size="small" onClick={toogleEdit}>
            {editMode ? "Done" : "Edit"}
          </Button>
        )}
      </div>
    </div>
  );
};
